"use client";

import React, { useState, useEffect, useContext } from 'react';
import { Printer, RotateCcw } from 'lucide-react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'axios';
import PrintableFolder from './NewTemplatesComponent/PrintableFolder';
import { errorAlert } from '../../Components/Alerts';
import { ApiLink } from '../../APILink/ApiLink';
import authHeader from '../../Auth/authHeader';
import { FiscalYearContext } from '../../FiscalYear/FiscalYearContext';
import { Documents, Periudha } from '../../OtherData/CoverData';
import './FolderTemplatesPage.scss';

export default function FolderTemplatesPage() {
  const headers = authHeader();
  const { fiscalYear } = useContext(FiscalYearContext);

  // Company data (read-only, fetched from API)
  const [companyName, setCompanyName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [logo, setLogo] = useState("");

  // Form data (user selections)
  const [documentType, setDocumentType] = useState('Dosja e Kompanisë');
  const [period, setPeriod] = useState('');

  useEffect(() => {
    fetchCompaniesData();
    // Set default period
    if (Periudha.length > 0) {
      setPeriod(`${fiscalYear} ${Periudha[0].name}`);
    }
  }, [fiscalYear]);

  const fetchCompaniesData = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + 'Companies/getCompanyDetails', { headers });
      setCompanyName(response.data[0].CompanyName);
      setCompanyId(response.data[0].IdentificationNumber);
      setCompanyAddress(response.data[0].Address);
      setEmail(response.data[0].Email);
      setTelephone(response.data[0].PhoneNumbers);
      setLogo(response.data[0].CompanyLogo);
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (error.response && error.response.data && error.response.data.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      }
    }
  };

  const handlePrint = () => {
    // Create a new window with just the A4 template for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Kopertina për printim | Accountree</title>
          <style>
            * {
              box-sizing: border-box;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            @page {
              size: A4;
              margin: 0;
            }
            
            html, body {
              width: 210mm;
              height: 297mm;
              margin: 0;
              padding: 0;
              background: transparent;
            }

            :root {
              --brand-900: #1f3a36;
              --brand-800: #284945;
              --brand-700: #3b6152;
              --brand-600: #4e7b67;
              --brand-border: #d4e2db;
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              color: var(--brand-900);
            }
            
            .page {
              display: flex;
              flex-direction: column;
              width: 210mm;
              height: 297mm;
              padding: 10mm;
              background-color: transparent;
              border: 1px solid var(--brand-border);
              line-height: 1.6;
              position: relative;
            }

            .page::before,
            .page::after {
              content: '';
              position: absolute;
              pointer-events: none;
            }

            .page::before {
              inset: 6mm;
              border: 1px solid #e6efe9;
            }

            .page::after {
              top: 8mm;
              right: 8mm;
              width: 34mm;
              height: 34mm;
              border-top: 2px solid #bfd1c8;
              border-right: 2px solid #bfd1c8;
            }
            
            .header {
              padding: 6px 0 16px;
              margin-bottom: 22px;
            }
            
            .header-top {
              display: grid;
              grid-template-columns: 84px minmax(0, 1fr);
              gap: 14px;
              align-items: start;
            }
            
            .logo-container {
              width: 84px;
              height: 84px;
              background: #fff;
              border: 1px solid #cfe0d8;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              box-shadow: inset 0 0 0 1px #f5f9f7;
            }
            
            .logo-img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              padding: 6px;
            }
            
            .header-info {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 2px;
              padding-top: 0;
            }
            
            .company-name {
              font-size: 24px;
              font-weight: 800;
              color: var(--brand-900);
              margin: 0;
              line-height: 1.2;
              text-transform: uppercase;
            }

            .company-subtitle {
              margin: 0;
              font-size: 11px;
              font-weight: 600;
              letter-spacing: 0.12em;
              text-transform: uppercase;
              color: var(--brand-600);
            }

            .company-subtitle-below {
              margin-top: 8px;
              font-size: 10px;
              letter-spacing: 0.14em;
              text-align: center;
            }
            
            .company-details {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              column-gap: 16px;
              row-gap: 8px;
              align-items: start;
              padding-top: 2px;
            }

            .details-column {
              display: flex;
              flex-direction: column;
              gap: 6px;
              align-self: start;
            }
            
            .detail-row {
              display: grid;
              grid-template-columns: 62px minmax(0, 1fr);
              gap: 6px;
              align-items: baseline;
              min-height: 20px;
            }
            
            .detail-label {
              font-size: 10px;
              font-weight: 700;
              color: var(--brand-600);
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            
            .detail-value {
              font-size: 12px;
              color: var(--brand-800);
              font-weight: 600;
              line-height: 1.35;
              word-break: break-word;
            }
            
            .header-divider {
              display: block;
              margin-top: 14px;
              height: 2px;
              background: linear-gradient(90deg, #284945 0%, #90ad9f 55%, transparent 100%);
            }
            
            .main {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 16px 0;
            }
            
            .decorative-box {
              position: relative;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 22px;
              padding: 28px 26px 30px;
              background: #fff;
              border: 1px solid #d2e1d9;
              border-radius: 10px;
              box-shadow: inset 0 0 0 1px #f1f7f4;
              max-width: 600px;
              width: 100%;
            }

            .decorative-box > * {
              position: relative;
              z-index: 2;
            }

            .decorative-box::before,
            .decorative-box::after {
              content: '';
              position: absolute;
              left: 22px;
              right: 22px;
              height: 1px;
              background: #e3ece7;
            }

            .decorative-box::before {
              top: 12px;
            }

            .decorative-box::after {
              bottom: 12px;
            }

            .document-badge {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-height: 26px;
              padding: 0 12px;
              border: 1px solid #c9d9d1;
              border-radius: 999px;
              font-size: 10px;
              font-weight: 700;
              letter-spacing: 0.14em;
              color: var(--brand-700);
              background: #f8fcfa;
              box-shadow: 0 1px 0 rgba(31, 58, 54, 0.06);
            }
            
            .document-section {
              position: relative;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 12px;
              width: 100%;
            }

            .document-section::before,
            .period-section::after {
              content: '';
              position: absolute;
              width: 24px;
              height: 24px;
              border-color: #cbdcd3;
              border-style: solid;
              pointer-events: none;
            }

            .document-section::before {
              top: -14px;
              left: -12px;
              border-width: 2px 0 0 2px;
            }
            
            .section-label {
              font-size: 11px;
              font-weight: 700;
              color: var(--brand-600);
              text-transform: uppercase;
              letter-spacing: 0.16em;
            }
            
            .title {
              font-size: 50px;
              font-weight: 900;
              color: var(--brand-900);
              text-align: center;
              margin: 0;
              line-height: 1.1;
              text-transform: uppercase;
              letter-spacing: -1px;
            }
            
            .divider {
              width: 160px;
              height: 1px;
              background: linear-gradient(90deg, transparent, #a9c0b3 20%, #a9c0b3 80%, transparent);
            }
            
            .period-section {
              position: relative;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 10px;
              width: 100%;
            }

            .period-section::after {
              right: -12px;
              bottom: -16px;
              border-width: 0 2px 2px 0;
            }
            
            .period {
              font-size: 34px;
              font-weight: 800;
              color: #284945;
              text-align: center;
              margin: 0;
              line-height: 1.1;
              text-transform: uppercase;
              letter-spacing: -0.5px;
            }
            
            .footer {
              margin-top: auto;
              padding-top: 14px;
            }
            
            .footer-divider {
              height: 1px;
              background: linear-gradient(90deg, transparent, #cdded6 18%, #cdded6 82%, transparent);
              margin-bottom: 12px;
            }
            
            .footer-content {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              font-size: 12px;
            }
            
            .footer-software {
              margin: 0;
              font-weight: 700;
              color: #284945;
              font-size: 11px;
              letter-spacing: 0.12em;
              text-transform: uppercase;
              text-align: center;
              white-space: nowrap;
            }
          </style>
        </head>
        <body>
          <div class="page">
            <header class="header">
              <div class="header-top">
                <div class="logo-container">
                  ${printData.logo ? `<img src="${ApiLink.PHOTO_URL}${printData.logo}" alt="Logo" class="logo-img">` : '<div></div>'}
                </div>
                <div class="header-info">
                  <h1 class="company-name">${printData.companyName || 'EMRI I KOMPANISË'}</h1>
                  <div class="company-details">
                    <div class="details-column">
                      <div class="detail-row">
                        <span class="detail-label">NUI:</span>
                        <span class="detail-value">${printData.companyId || '—'}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Telefon:</span>
                        <span class="detail-value">${printData.telephone || '—'}</span>
                      </div>
                    </div>
                    <div class="details-column">
                      <div class="detail-row">
                        <span class="detail-label">Adresa:</span>
                        <span class="detail-value">${printData.companyAddress || '—'}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Email:</span>
                        <span class="detail-value">${printData.email || '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="header-divider"></div>
              <p class="company-subtitle company-subtitle-below">Dokumentacion për arkivim</p>
            </header>
            
            <main class="main">
              <div class="decorative-box">
                <div class="document-badge">KOPERTINË DOKUMENTI</div>
                <div class="document-section">
                  <span class="section-label">DOKUMENT</span>
                  <h2 class="title">${printData.documentType || 'LLOJI I DOKUMENTIT'}</h2>
                </div>
                
                <div class="divider"></div>
                
                <div class="period-section">
                  <span class="section-label">PERIUDHA</span>
                  <h3 class="period">${printData.period || 'MUAJI/VITI'}</h3>
                </div>
              </div>
            </main>
            
            <footer class="footer">
              <div class="footer-divider"></div>
              <div class="footer-content">
                <p class="footer-software">Gjeneruar nga Accountree</p>
              </div>
            </footer>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => window.close(), 100);
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleReset = () => {
    setDocumentType('Dosja e Kompanisë');
    if (Periudha.length > 0) {
      setPeriod(`${fiscalYear} ${Periudha[0].name}`);
    }
  };

  const printData = {
    documentType,
    period,
    companyName,
    companyId,
    companyAddress,
    email,
    telephone,
    logo
  };

  return (
    <div className="FolderTemplates__container">
      {/* Left Panel - Configuration (No Print) */}
      <div className="FolderTemplates__leftPanel no-print">
        <div className="FolderTemplates__header">
          <h1>Kopertinat</h1>
          <p>Zgjidh dokumentin dhe periudhën për printim</p>
        </div>
        
        <div className="FolderTemplates__content">
          <div className="FolderTemplates__formSection">
            <Autocomplete
              id="document-type"
              freeSolo
              options={Documents.map((option) => option.name)}
              value={documentType}
              onChange={(event, newValue) => {
                setDocumentType(newValue || 'Dosja e Kompanisë');
              }}
              onInputChange={(event, newInputValue) => {
                setDocumentType(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Zgjidh dokumentin"
                  fullWidth
                />
              )}
            />
          </div>

          <div className="FolderTemplates__formSection">
            <TextField
              select
              label="Zgjidh periudhën"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              fullWidth
            >
              {Periudha.map((data) => (
                <MenuItem key={data.id} value={`${fiscalYear} ${data.name}`}>
                  {`${fiscalYear} ${data.name}`}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div className="FolderTemplates__infoSection">
            <h3>Informacioni i Kompanisë</h3>
            <div className="FolderTemplates__infoItem">
              <span className="FolderTemplates__infoLabel">Emri:</span>
              <span className="FolderTemplates__infoValue">{companyName}</span>
            </div>
            <div className="FolderTemplates__infoItem">
              <span className="FolderTemplates__infoLabel">NUI:</span>
              <span className="FolderTemplates__infoValue">{companyId}</span>
            </div>
            <div className="FolderTemplates__infoItem">
              <span className="FolderTemplates__infoLabel">Adresa:</span>
              <span className="FolderTemplates__infoValue">{companyAddress}</span>
            </div>
            <div className="FolderTemplates__infoItem">
              <span className="FolderTemplates__infoLabel">Telefon:</span>
              <span className="FolderTemplates__infoValue">{telephone}</span>
            </div>
            <div className="FolderTemplates__infoItem">
              <span className="FolderTemplates__infoLabel">Email:</span>
              <span className="FolderTemplates__infoValue">{email}</span>
            </div>
          </div>
        </div>

        <div className="FolderTemplates__footer">
          <Button 
            variant="outlined" 
            onClick={handleReset}
            className="FolderTemplates__buttonReset"
            startIcon={<RotateCcw />}
          >
            Rivendos
          </Button>
          <Button 
            variant="contained"
            onClick={handlePrint} 
            className="FolderTemplates__buttonPrint"
            startIcon={<Printer />}
          >
            Printo
          </Button>
        </div>
      </div>

      {/* Right Panel - Preview (Becomes Print Content) */}
      <div className="FolderTemplates__rightPanel">
        <div className="FolderTemplates__previewWrapper">
          <PrintableFolder data={printData} />
        </div>
      </div>
    </div>
  );
}
